// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';

// Angular Material
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';

// Third party
import { takeUntil } from 'rxjs';

// Local library
import {
  AccountFacade,
  BetSummaryService,
  IBetSummary,
  IBetSummaryRow
} from '@amwager/account/data-access';
import {
  MatNotificationService,
  NumberWithCommasPipe,
  UnsubscribeObservableService,
  TranslatePipe
} from '@amwager/util';
import { ICustomerBetSummaryModel } from '@amwager/util-ts';

/**
 * Account bet summary — date-range filtered wager totals grouped by
 * track/pool with derived percent and profit/loss. Ported from the
 * legacy statements "Bet Summary" tab; form + calculations live in
 * BetSummaryService.
 */
@Component({
  selector: 'feature-mat-account-bet-summary',
  templateUrl: './bet-summary.component.html',
  styleUrls: ['./bet-summary.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    NumberWithCommasPipe,
    TranslatePipe
  ]
})
export class BetSummaryComponent
  extends UnsubscribeObservableService
  implements OnInit, OnDestroy {

  private readonly HOST = 'BetSummaryComponent';

  readonly maxDate = new Date();

  getBetSummaryForm!: FormGroup;
  rows: IBetSummaryRow[] = [];

  constructor(
    private readonly betSummaryService: BetSummaryService,
    private readonly accountFacade: AccountFacade,
    private readonly matNotificationService: MatNotificationService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.getBetSummaryForm = this.betSummaryService.buildForm();

    // Initial fetch + subsequent results
    this.fetchBetSummary(`${this.HOST}:ngOnInit`);
    this.accountFacade.getBetSummary$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((betSummary?: IBetSummary) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response = betSummary?.response as any;
        const error = response?.error ?? response?.Error;
        if (error) {
          this.rows = [];
          this.showAlert('Error', typeof error === 'string' ? error : error?.Message);
        } else {
          // Response may be a bare array, { CustomerBetSummary } or wrapped in Model
          const container = response?.Model ?? response;
          const records: ICustomerBetSummaryModel[] =
            (Array.isArray(container) ? container : container?.CustomerBetSummary) ?? [];
          this.rows = this.betSummaryService.buildRows(records);
        }
        this.cdRef.markForCheck();
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  /**
   * Search button — validates the range then dispatches the fetch
   * @returns {void}
   */
  searchBetSummary(): void {
    if (this.betSummaryService.isDateRangeInvalid(this.getBetSummaryForm)) {
      this.showAlert('Error', 'Date To should not be less than Date From.');
      return;
    }
    this.fetchBetSummary(`${this.HOST}:searchBetSummary`);
  }

  /**
   * trackBy for the bet summary rows
   * @param {number} index Loop index
   * @param {IBetSummaryRow} row Bet summary row
   * @returns {string} Stable key
   */
  trackByRow(index: number, row: IBetSummaryRow): string {
    return `${row.eventName}-${row.poolId}-${index}`;
  }

  private fetchBetSummary(source: string): void {
    this.accountFacade.getBetSummary(
      source,
      this.betSummaryService.buildRequest(this.getBetSummaryForm)
    );
  }

  private showAlert(header: string, message: string): void {
    this.matNotificationService.showAlert(header, message ?? '', '', '', 'Close');
  }
}
